/**
 * Janela de edição de um anúncio do Mercado Livre: lê o anúncio na hora no canal e envia só o que mudou.
 * Título e descrição de anúncio de catálogo vêm da ficha do ML e ficam travados.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { ExternalLink, RefreshCw, X } from 'lucide-react';
import type { ListingDetail, ListingUpdateBody } from '@berytus/contracts';
import { api, brl, int } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag, Thumb } from '../components/ui.tsx';
import { can } from '@berytus/contracts';

const STATUS: Record<string, { label: string; tone: string }> = {
  active: { label: 'Ativo', tone: 'ok' },
  paused: { label: 'Pausado', tone: 'warn' },
  closed: { label: 'Finalizado', tone: 'off' },
  under_review: { label: 'Em revisão', tone: 'err' },
  inactive: { label: 'Inativo', tone: 'off' },
};
const SUB: Record<string, string> = {
  out_of_stock: 'sem estoque',
  deleted: 'excluído',
  forbidden: 'proibido',
  waiting_for_patch: 'aguardando correção',
  held: 'retido',
  freezed: 'congelado',
  suspended: 'suspenso',
  picture_download_pending: 'fotos pendentes',
};
const num = (v: string) => Number(v.replace(/\./g, '').replace(',', '.'));
const money = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function ListingEditor({ listingId, onClose }: { listingId: string; onClose: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const canEdit = can(me.tenant.access, 'listings', 'write') && !me.impersonating;
  const q = useQuery({ queryKey: ['meli', 'remote', listingId], queryFn: () => api<ListingDetail>(`/channels/listings/${listingId}/remote`), staleTime: 0 });
  const [f, setF] = useState<{ title: string; price: string; quantity: string; sku: string; description: string; vars: Record<number, { price: string; quantity: string }> } | null>(null);
  useEffect(() => {
    const d = q.data;
    if (!d) return;
    setF({
      title: d.title, price: money(d.price), quantity: String(d.availableQuantity), sku: d.sellerSku ?? '', description: d.description ?? '',
      vars: Object.fromEntries(d.variations.map((v) => [v.id, { price: money(v.price), quantity: String(v.availableQuantity) }])),
    });
  }, [q.data]);

  const done = () => { qc.invalidateQueries({ queryKey: ['meli'] }); };
  const save = useMutation({
    mutationFn: (body: ListingUpdateBody) => api<{ updated: string[] }>(`/channels/listings/${listingId}`, { method: 'PATCH', json: body }),
    onSuccess: done,
  });
  const refresh = useMutation({ mutationFn: () => api(`/channels/listings/${listingId}/refresh`, { method: 'POST' }), onSuccess: done });
  const remove = useMutation({
    mutationFn: async () => {
      const r = await api<{ ok: number; failed: { externalId: string; error: string }[] }>('/channels/listings/bulk-status', { method: 'POST', json: { ids: [listingId], status: 'deleted' } });
      if (r.failed.length) throw new Error(r.failed[0]!.error);
    },
    onSuccess: () => { done(); onClose(); },
  });
  const askRemove = () => {
    if (confirm('Excluir este anúncio no Mercado Livre? Ele é finalizado e some do seu painel do Mercado Livre. Não tem volta.')) remove.mutate();
  };

  const d = q.data;
  const changes = (): ListingUpdateBody => {
    if (!d || !f) return {};
    const b: ListingUpdateBody = {};
    if (!d.isCatalog && f.title.trim() !== d.title) b.title = f.title.trim();
    if (!d.variations.length) {
      if (num(f.price) !== d.price) b.price = num(f.price);
      if (Number(f.quantity) !== d.availableQuantity) b.quantity = Number(f.quantity);
      if (f.sku.trim() && f.sku.trim() !== (d.sellerSku ?? '')) b.sku = f.sku.trim();
    } else {
      const vs = d.variations.flatMap((v) => {
        const c = f.vars[v.id]!;
        const out: { id: number; price?: number; quantity?: number } = { id: v.id };
        if (num(c.price) !== v.price) out.price = num(c.price);
        if (Number(c.quantity) !== v.availableQuantity) out.quantity = Number(c.quantity);
        return out.price !== undefined || out.quantity !== undefined ? [out] : [];
      });
      if (vs.length) b.variations = vs;
    }
    if (!d.isCatalog && f.description !== (d.description ?? '')) b.description = f.description;
    return b;
  };
  const pending = changes();
  const dirty = Object.keys(pending).length > 0;
  const setStatus = (status: 'active' | 'paused' | 'closed') => {
    if (status === 'closed' && !confirm('Finalizar o anúncio? No Mercado Livre isso não tem volta: para vender de novo, é preciso republicar.')) return;
    save.mutate({ status });
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '5vh', left: '50%', transform: 'translateX(-50%)', width: 'min(720px, calc(100vw - 32px))', maxHeight: '90vh', overflow: 'auto', zIndex: 60, display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>Editar anúncio</h2></Dialog.Title>
            <span style={{ flex: 1 }} />
            <button className="btn sm quiet" type="button" disabled={refresh.isPending || q.isFetching} onClick={() => { refresh.mutate(); q.refetch(); }} title="Lê de novo no Mercado Livre">
              <RefreshCw size={14} /> Atualizar
            </button>
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>

          {q.isPending || !f ? (q.error ? <ErrorBox error={q.error} /> : <Loading />) : d && (
            <>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <Thumb src={d.thumbnail} alt="" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="small muted">
                    {d.permalink ? <a className="ext" href={d.permalink} target="_blank" rel="noreferrer">{d.externalId} <ExternalLink size={11} /></a> : d.externalId}
                    {' · '}{d.listingType === 'gold_pro' ? 'Premium' : d.listingType === 'gold_special' ? 'Clássico' : d.listingType}
                    {d.isCatalog && ' · catálogo'}
                    {d.soldQuantity != null && ` · ${int(d.soldQuantity)} vendido(s)`}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                    <Tag tone={STATUS[d.status]?.tone ?? 'off'}>{STATUS[d.status]?.label ?? d.status}</Tag>
                    {d.subStatus.map((s) => <Tag key={s} tone="warn">{SUB[s] ?? s}</Tag>)}
                  </div>
                </div>
                {canEdit && d.status !== 'closed' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    {d.status === 'active'
                      ? <button className="btn sm" type="button" disabled={save.isPending} onClick={() => setStatus('paused')}>Pausar</button>
                      : <button className="btn sm primary" type="button" disabled={save.isPending} onClick={() => setStatus('active')}>Ativar</button>}
                    <button className="btn sm quiet" type="button" style={{ color: 'var(--err)' }} disabled={save.isPending} onClick={() => setStatus('closed')}>Finalizar</button>
                    <button className="btn sm quiet" type="button" style={{ color: 'var(--err)' }} disabled={remove.isPending} onClick={askRemove}>Excluir</button>
                  </div>
                )}
                {canEdit && d.status === 'closed' && (
                  <button className="btn sm quiet" type="button" style={{ color: 'var(--err)' }} disabled={remove.isPending} onClick={askRemove}>{remove.isPending ? 'Excluindo…' : 'Excluir'}</button>
                )}
              </div>
              {d.status === 'closed' && <div className="notice small">Anúncio finalizado: o Mercado Livre não aceita mais alterações nele. Excluir tira ele também do seu painel do Mercado Livre.</div>}
              {remove.error && <ErrorBox error={remove.error} />}

              <fieldset disabled={!canEdit || d.status === 'closed' || save.isPending} style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 12 }}>
                <div className="field">
                  <label htmlFor="le-title">Título</label>
                  <input className="input" id="le-title" maxLength={200} disabled={d.isCatalog} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
                  <span className="small muted">
                    {d.isCatalog ? 'Anúncio de catálogo: o título vem da ficha do Mercado Livre.' : `${f.title.length} caracteres. O Mercado Livre pode recusar a troca de título em anúncio que já vendeu.`}
                  </span>
                </div>

                {d.variations.length === 0 ? (
                  <div className="cols" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    <div className="field"><label htmlFor="le-price">Preço (R$)</label><input className="input" id="le-price" inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
                    <div className="field"><label htmlFor="le-qty">Estoque no anúncio</label><input className="input" id="le-qty" inputMode="numeric" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value.replace(/\D/g, '') })} /></div>
                    <div className="field"><label htmlFor="le-sku">SKU</label><input className="input" id="le-sku" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} /></div>
                  </div>
                ) : (
                  <div>
                    <div className="small muted" style={{ marginBottom: 6 }}>Variações</div>
                    <table className="simple">
                      <thead><tr><th>Variação</th><th>SKU</th><th className="num">Preço (R$)</th><th className="num">Estoque</th></tr></thead>
                      <tbody>
                        {d.variations.map((v) => (
                          <tr key={v.id}>
                            <td>{v.label}</td>
                            <td className="small">{v.sku ?? '—'}</td>
                            <td className="num"><input className="input" style={{ width: 110, textAlign: 'right' }} inputMode="decimal" aria-label={`Preço ${v.label}`} value={f.vars[v.id]!.price}
                              onChange={(e) => setF({ ...f, vars: { ...f.vars, [v.id]: { ...f.vars[v.id]!, price: e.target.value } } })} /></td>
                            <td className="num"><input className="input" style={{ width: 90, textAlign: 'right' }} inputMode="numeric" aria-label={`Estoque ${v.label}`} value={f.vars[v.id]!.quantity}
                              onChange={(e) => setF({ ...f, vars: { ...f.vars, [v.id]: { ...f.vars[v.id]!, quantity: e.target.value.replace(/\D/g, '') } } })} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="field">
                  <label htmlFor="le-desc">Descrição</label>
                  <textarea className="input" id="le-desc" rows={6} style={{ height: 'auto', padding: 10, resize: 'vertical' }} disabled={d.isCatalog}
                    value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={d.isCatalog ? 'Vem da ficha do catálogo' : 'Sem descrição'} />
                  <span className="small muted">Só texto; o Mercado Livre não aceita HTML nem links na descrição.</span>
                </div>
              </fieldset>

              <p className="small muted" style={{ margin: 0 }}>
                As alterações vão na hora para o Mercado Livre. Preço e estoque mudados aqui valem para o anúncio; a tabela de preços da Berytus não muda.
              </p>
              {save.error && <ErrorBox error={save.error} />}
              {save.isSuccess && !dirty && <div className="notice small">Alterado no Mercado Livre{save.data?.updated.length ? '' : ' (nada mudou)'}.</div>}
              {canEdit && d.status !== 'closed' && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn primary" type="button" disabled={!dirty || save.isPending} onClick={() => save.mutate(pending)}>
                    {save.isPending ? 'Enviando…' : 'Salvar no Mercado Livre'}
                  </button>
                  <button className="btn" type="button" onClick={onClose}>Fechar</button>
                  {dirty && <span className="small muted">Muda: {[pending.title && 'título', pending.price !== undefined && `preço ${brl(pending.price)}`, pending.quantity !== undefined && 'estoque', pending.sku && 'SKU', pending.variations && 'variações', pending.description !== undefined && 'descrição'].filter(Boolean).join(', ')}</span>}
                </div>
              )}
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
